import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ActiveHat, UserCapabilities } from '@wwaave/shared';
import type {
  FeatureFlagKey,
  FeatureVisibility,
  LaunchPhase,
} from '@wwaave/shared/feature-flags';
import { createContext, type ReactNode, useCallback, useContext, useMemo } from 'react';

import { ApiError, fetchOk } from './api';
import { track } from './telemetry';

/**
 * Auth context — populated from `GET /api/auth/me`.
 *
 * The cookie itself is HttpOnly so the SPA never touches it directly;
 * instead the worker round-trip resolves the user shape on app boot.
 *
 * Wave 1 dual-role: the response also carries `capabilities` (which hats
 * this user can wear) and `activeHat` (which one is currently selected).
 * The hat switcher in AppHeader calls `setActiveHat()` below; the auth
 * context updates optimistically and refreshes from the server.
 *
 * `csrf` is read from the non-HttpOnly `wwaave_csrf` cookie at call time
 * (not held in state) and sent on every mutating fetch via `X-CSRF-Token`.
 */
export type AuthUser = {
  id: string;
  email: string;
  role: 'artist' | 'host' | 'admin';
  displayName: string;
  status: 'pending' | 'active' | 'suspended' | 'deleted' | 'pending_close' | 'closed';
  closingAt?: string | null;
  colorSchemePreference: 'light' | 'dark' | 'system';
};

type AuthContextValue = {
  user: AuthUser | null;
  capabilities: UserCapabilities | null;
  activeHat: ActiveHat | null;
  isLoading: boolean;
  /** Force re-fetch of /me — useful after sign-in / sign-up / sign-out. */
  refresh: () => Promise<void>;
  /** POST /api/auth/sign-out, clear cache, redirect to / via the caller. */
  signOut: () => Promise<void>;
  /** Switch hats. Validates server-side; throws ApiError on 403. */
  setActiveHat: (hat: ActiveHat) => Promise<void>;
};

const AuthContext = createContext<AuthContextValue>({
  user: null,
  capabilities: null,
  activeHat: null,
  isLoading: false,
  refresh: async () => {},
  signOut: async () => {},
  setActiveHat: async () => {},
});

const ME_KEY = ['auth', 'me'] as const;

type MeResponse = {
  user: AuthUser | null;
  capabilities: UserCapabilities | null;
  activeHat: ActiveHat | null;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ME_KEY,
    queryFn: async (): Promise<MeResponse> => {
      const res = await fetch('/api/auth/me', {
        method: 'GET',
        credentials: 'include',
      });
      if (!res.ok) return { user: null, capabilities: null, activeHat: null };
      const json = (await res.json()) as MeResponse;
      return json;
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ME_KEY });
  }, [queryClient]);

  const signOutMutation = useMutation({
    mutationFn: async () => {
      // Emit before the request so the event flushes while the session
      // (and the cookie's CSRF token) is still valid. detachAuth on the
      // TelemetryProvider unmount handles the trailing flush as a backup.
      track('auth.signed_out', {});
      await fetch('/api/auth/sign-out', {
        method: 'POST',
        credentials: 'include',
        headers: csrfHeaders(),
      });
    },
    onSuccess: async () => {
      queryClient.setQueryData<MeResponse>(ME_KEY, {
        user: null,
        capabilities: null,
        activeHat: null,
      });
      await queryClient.invalidateQueries({ queryKey: ME_KEY });
    },
  });

  // Depend on `mutateAsync` (stable), never the mutation object: useMutation
  // returns a new object on every state change, which made `signOut` change
  // identity mid-call and re-fired /sign-out's effect in a loop (React #185).
  const { mutateAsync: signOutAsync } = signOutMutation;
  const signOut = useCallback(async () => {
    await signOutAsync();
  }, [signOutAsync]);

  const setActiveHat = useCallback(
    async (hat: ActiveHat) => {
      // Optimistic local flip so the UI updates immediately. Failure paths
      // refetch /me which restores the canonical hat the server is willing
      // to serve.
      const previous = queryClient.getQueryData<MeResponse>(ME_KEY);
      if (previous) {
        queryClient.setQueryData<MeResponse>(ME_KEY, { ...previous, activeHat: hat });
      }
      try {
        await fetchOk('/api/auth/active-hat', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
          body: JSON.stringify({ hat }),
        });
        track('auth.hat_switched', { newHat: hat });
        await queryClient.invalidateQueries({ queryKey: ME_KEY });
      } catch (err) {
        // Reset to whatever the server says.
        await queryClient.invalidateQueries({ queryKey: ME_KEY });
        if (err instanceof ApiError) throw err;
        throw new ApiError(0, err instanceof Error ? err.message : 'Could not switch hats.');
      }
    },
    [queryClient],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user: data?.user ?? null,
      capabilities: data?.capabilities ?? null,
      activeHat: data?.activeHat ?? null,
      isLoading,
      refresh,
      signOut,
      setActiveHat,
    }),
    [data, isLoading, refresh, signOut, setActiveHat],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}

/**
 * Read the `wwaave_csrf` cookie value (set by the worker on sign-in/sign-up)
 * and shape it into a header object suitable for spreading into a fetch init.
 *
 * Returns an empty object when no cookie is present — the worker will reject
 * the mutation, surfacing the bug at the API layer rather than silently here.
 */
export function csrfHeaders(): Record<string, string> {
  if (typeof document === 'undefined') return {};
  const match = document.cookie.match(/(?:^|; )wwaave_csrf=([^;]+)/);
  if (!match) return {};
  return { 'X-CSRF-Token': decodeURIComponent(match[1] ?? '') };
}

/**
 * Read-only snapshot of platform launch state. Sourced from the unauthenticated
 * `/api/__public__/launch-state` endpoint so marketing pages (which may render
 * outside `AuthProvider`) can also consult it. The KV-backed worker route fans
 * the same blob to every caller, so caching for 30 s is a safe default — admin
 * mutations invalidate `['admin', 'launch']` but this query is allowed to lag
 * by up to KV's global propagation window.
 */
export type LaunchStateSnapshot = {
  phase: LaunchPhase;
  features: Record<FeatureFlagKey, FeatureVisibility>;
};

const LAUNCH_STATE_KEY = ['public', 'launch-state'] as const;

export function useLaunchState(): {
  data: LaunchStateSnapshot | undefined;
  isLoading: boolean;
} {
  const query = useQuery<LaunchStateSnapshot | undefined>({
    queryKey: LAUNCH_STATE_KEY,
    queryFn: async (): Promise<LaunchStateSnapshot | undefined> => {
      const res = await fetch('/api/__public__/launch-state', {
        method: 'GET',
        credentials: 'include',
      });
      if (!res.ok) return undefined;
      const json = (await res.json()) as LaunchStateSnapshot;
      return json;
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  return { data: query.data, isLoading: query.isLoading };
}

/**
 * Convenience read of the pre-launch funnel state, derived from `useLaunchState`.
 *
 *   - `waitlistMode`  — the `waitlist_mode` flag is `all`: account CTAs should
 *     route to the waitlist capture instead of the (closed) sign-up form.
 *   - `signupsOpen`   — the `public_signups` flag is `all`: an anonymous visitor
 *     can create an account right now. (`founding_cohort` doesn't open sign-ups
 *     to a brand-new visitor, so only `all` counts as open here.)
 *
 * Both default to `false` while the query is in flight / on error — callers
 * should treat `isLoading` as "don't branch yet". The server (routes/auth.ts)
 * remains the hard gate; this only shapes the client funnel.
 */
export function useWaitlistMode(): {
  waitlistMode: boolean;
  signupsOpen: boolean;
  isLoading: boolean;
  loaded: boolean;
} {
  const { data, isLoading } = useLaunchState();
  return {
    waitlistMode: data?.features['waitlist_mode'] === 'all',
    signupsOpen: data?.features['public_signups'] === 'all',
    isLoading,
    loaded: data !== undefined,
  };
}
